import React, { useRef, useState } from "react";
import { boomAngleDeg, hookHeightM } from "./liftPlanCalc";

// =============================================================================
// Lift plan drawings — T-436
// =============================================================================
// PLAN + ELEVATION, the orthographic pair every crane company issues (Borger's
// Park Ridge study runs a 1:200 plan and 1:250 elevations). Chosen over 3D
// deliberately: this is what prints, and it's what shows radius, boom angle
// and hook height without ambiguity.
//
// The plan is DIRECTLY MANIPULABLE — drag the compound, the pick point, the
// pole sections; rotate anything selected. Dragging the pick or place marker
// rewrites the radius, so "check the reach" is a drag rather than a form field.
//
// Everything shares ONE scale, so compound, crane and radii stay honest against
// each other. Schematic layout, real geometry.
// =============================================================================

export type PlacedItem = {
  id: string;
  // Site furniture for the mud map. James, 01/08: "I need to label laydown
  // areas, site compound, draw in other things in the mud map" — a lift plan
  // that only shows the crane and the pole isn't site specific enough to submit.
  kind: "pole_section" | "lattice_module" | "plant" | "note"
      | "laydown" | "platform" | "shelter" | "hardstand" | "access" | "keepout";
  x_m: number;            // metres from the crane slew centre, +x right
  y_m: number;            // +y down
  rot_deg: number;
  length_m: number;
  width_m: number;
  label?: string;
  /** Nudge the label clear of anything covering it; a leader is drawn back. */
  label_dx_m?: number;
  label_dy_m?: number;
  // Where the hook attaches along the piece, 0 = tail end, 1 = head end.
  // Concrete poles are tapered and usually hooked off-centre; a dual-chain
  // centre lift is 0.5. Drag the dot on the plan to move it.
  lift_frac?: number;
};

export type CanvasState = {
  scale_px_per_m: number;
  compound_m: number;
  compound_rotation_deg: number;
  compound_x_m: number;              // compound centre, from crane
  compound_y_m: number;
  structure: "monopole" | "lattice" | "headframe" | "other";
  // The pole is rarely dead centre in the pad. A couple of metres off changes
  // the radius and therefore the capacity, so it has to be settable rather than
  // assumed (James, 01/08). Offsets are in COMPOUND axes, so they rotate with
  // the compound and read straight off the FC setout.
  structure_dx_m: number;
  structure_dy_m: number;
  /** Nudge the ℄ STRUCTURE callout clear of whatever is covering it. */
  structure_label_dx_m?: number;
  structure_label_dy_m?: number;
  leg_spread_m: number;
  slew_deg: number;
  pick_x_m: number;                  // pick point, from crane
  pick_y_m: number;
  exclusion_r_m: number;
  items: PlacedItem[];
  /** View pan, in metres. Drag empty canvas to move the whole drawing. */
  pan_x_m?: number;
  pan_y_m?: number;
  /** Carrier heading — back it in, sit it side on, whatever the pad allows. */
  crane_heading_deg?: number;
  /** Scanned FC / aerial behind the drawing, and how it's placed. */
  bg_scale_px_per_m?: number;      // image pixels per metre
  bg_x_m?: number;                 // image centre, from the crane
  bg_y_m?: number;
  bg_opacity?: number;
  ground_gradient_pct: number;
  structure_height_m: number;
  background_url?: string | null;
};

export const DEFAULT_CANVAS: CanvasState = {
  scale_px_per_m: 11,
  compound_m: 10,
  compound_rotation_deg: 0,
  compound_x_m: 12.5,
  compound_y_m: 0,
  structure: "monopole",
  structure_dx_m: 0,
  structure_dy_m: 0,
  leg_spread_m: 6,
  slew_deg: 0,
  pick_x_m: 0,
  pick_y_m: -10,
  exclusion_r_m: 0,
  items: [],
  ground_gradient_pct: 0,
  structure_height_m: 30,
  background_url: null,
};

/** Older saved plans predate items/xy — fill the gaps without losing anything. */
export function normaliseCanvas(raw: any): CanvasState {
  const c = { ...DEFAULT_CANVAS, ...(raw ?? {}) };
  if (c.compound_x_m == null) c.compound_x_m = raw?.place_radius_m ?? 12.5;
  if (c.compound_y_m == null) c.compound_y_m = 0;
  if (c.pick_x_m == null || c.pick_y_m == null) {
    const r = raw?.pick_radius_m ?? 10;
    c.pick_x_m = 0; c.pick_y_m = -r;
  }
  if (Array.isArray(c.items)) {
    c.items = c.items.map((it: PlacedItem) => ({ ...it, lift_frac: it.lift_frac ?? 0.5 }));
  }
  if (!Array.isArray(c.items)) {
    // Migrate the old laydown{count,length} shape into real placed sections.
    const ld = raw?.laydown;
    c.items = ld?.count
      ? Array.from({ length: ld.count }).map((_, i) => ({
          id: `sec-${i}`, kind: "pole_section" as const,
          x_m: c.pick_x_m, y_m: c.pick_y_m - 1 + i * 1.2,
          rot_deg: 0, length_m: ld.length_m ?? 12, width_m: 0.9,
          label: `Section ${i + 1}`,
        }))
      : [];
  }
  return c;
}

/** Sentinel id so the compound can be "selected" alongside placed items. */
export const COMPOUND_ID = "__compound__";

export const radiusOf = (x: number, y: number) => Math.sqrt(x * x + y * y);

/**
 * Where the structure actually stands, in crane coordinates. The offset is held
 * in COMPOUND axes so it reads straight off the FC setout ("3.0 m off the north
 * fence"), and rotates with the compound. Capacity is judged at the radius to
 * THIS, not to the middle of the pad — a couple of metres is the difference
 * between passing and not (James, 01/08).
 */
export function structurePos(st: CanvasState): { x: number; y: number } {
  const rad = ((st.compound_rotation_deg ?? 0) * Math.PI) / 180;
  const dx = st.structure_dx_m ?? 0, dy = st.structure_dy_m ?? 0;
  return {
    x: st.compound_x_m + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: st.compound_y_m + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

/** Setback from each compound fence, for the drawing note and a sanity check. */
export function fenceSetbacks(st: CanvasState) {
  const h = (st.compound_m ?? 10) / 2;
  const dx = st.structure_dx_m ?? 0, dy = st.structure_dy_m ?? 0;
  return { west: h + dx, east: h - dx, north: h + dy, south: h - dy };
}

/** Sensible starting size + name for each thing you can drop on the mud map. */
const ITEM_SPEC: Record<PlacedItem["kind"], { l: number; w: number; label: string }> = {
  pole_section:   { l: 12,  w: 0.9, label: "Section" },
  lattice_module: { l: 6,   w: 6,   label: "Module" },
  plant:          { l: 6,   w: 2.5, label: "Plant" },
  note:           { l: 4,   w: 1,   label: "Note" },
  laydown:        { l: 12,  w: 6,   label: "Laydown area" },
  platform:       { l: 6.5, w: 3.8, label: "Equipment platform" },
  shelter:        { l: 3.6, w: 2.8, label: "Shelter" },
  hardstand:      { l: 14,  w: 10,  label: "Crane hardstand" },
  access:         { l: 20,  w: 4,   label: "Access track" },
  keepout:        { l: 8,   w: 8,   label: "Exclusion / keep clear" },
};

export const newItem = (kind: PlacedItem["kind"], n: number): PlacedItem => {
  const s = ITEM_SPEC[kind] ?? ITEM_SPEC.note;
  const numbered = kind === "pole_section" || kind === "lattice_module";
  return {
    id: `${kind}-${Date.now()}-${n}`,
    kind,
    x_m: kind === "lattice_module" ? 2 : 0,
    y_m: -12 - n * 1.4,
    rot_deg: 0,
    length_m: s.l,
    width_m: s.w,
    label: numbered ? `${s.label} ${n}` : s.label,
    lift_frac: 0.5,
  };
};

/** Fill / stroke / hatch per item kind, so the mud map reads at a glance. */
export const ITEM_STYLE: Record<string, { fill: string; stroke: string; dash?: string; op?: number }> = {
  pole_section:   { fill: "#D8D5C9", stroke: "#8E8B80" },
  lattice_module: { fill: "#E4E1D6", stroke: "#8E8B80" },
  plant:          { fill: "#F2C21A", stroke: "#A8880C", op: 0.55 },
  note:           { fill: "none",    stroke: "#8E8B80", dash: "3 3" },
  laydown:        { fill: "#EDE9DA", stroke: "#A79A6B", dash: "6 3", op: 0.7 },
  platform:       { fill: "#D6DEE4", stroke: "#6E8494" },
  shelter:        { fill: "#C9D3DA", stroke: "#6E8494" },
  hardstand:      { fill: "#E6E3D8", stroke: "#9A968A", dash: "2 3", op: 0.6 },
  access:         { fill: "#EFEADF", stroke: "#A79A6B", dash: "8 4", op: 0.8 },
  keepout:        { fill: "#E24B4A", stroke: "#C0392B", dash: "5 4", op: 0.09 },
};

const INK = "#3F3E3A", MUTED = "#8E8B80", LINE = "#C9C6BB", PAPER = "#F7F6F2";
// Muted industrial palette. The crane stays recognisably yellow but drops the
// high-vis saturation that made the drawing look like a toy; everything else is
// greyscale so the only colour carrying meaning is the capacity ring.
const YEL = "#E0B93F", YEL_D = "#9A7F1E", STEEL = "#E4E2DB", ACCENT = "#FF6A1A";
// Capacity traffic light for the swing circle, and a cool slate for the
// exclusion boundary so the two never read as the same kind of warning.
const BAND_COL = { ok: "#2E8B57", caution: "#C8952A", over: "#C0392B", unknown: "#8E8B80" } as const;
const SLATE = "#5B6B7A";
export type CapacityBand = keyof typeof BAND_COL;

// -----------------------------------------------------------------------------
// All-terrain crane, plan view, drawn in METRES.
//
// Rebuilt 01/08 — James: "we are in 2026 and you are AI, draw me a crane thats
// more alike to a real crane not a box". The old glyph was three rectangles.
// This is a 5-axle AT: carrier with individual wheels, outrigger beams telescoped
// out to pads, slewing superstructure with counterweight slab, operator's cab
// offset to the left of the boom, and a four-section telescopic boom that tapers
// to the head. Everything is metre-scaled off the real outrigger spread, so it
// stays honest against the compound at any zoom.
// -----------------------------------------------------------------------------
function CranePlan({ cx, cy, px, slew, outW, outL, heading = 0 }: {
  cx: number; cy: number; px: number; slew: number; outW: number; outL: number;
  /** Carrier heading — back it in, sit it side on, whatever the pad allows.
      Slew is applied on top, so it stays relative to the carrier (James, 01/08). */
  heading?: number;
}) {
  const m = (v: number) => v * px;
  const hw = (outW * px) / 2, hl = (outL * px) / 2;
  const padW = m(2.0), padL = m(1.3);
  const carW = m(2.75), carL = m(13.2);
  const pads = [[-hw, -hl], [hw, -hl], [-hw, hl], [hw, hl]];
  const axles = [-0.40, -0.27, 0.09, 0.22, 0.35];   // fractions of carrier length
  const tyreL = m(1.35), tyreW = m(0.55);
  return (
    <g style={{ pointerEvents: "none" }} transform={`rotate(${heading} ${cx} ${cy})`}>
      {/* Outrigger beams — telescoped out to the pad centres */}
      <rect x={cx - hw} y={cy - m(0.28) - hl * 0.62} width={hw * 2} height={m(0.55)}
        fill="#E0B417" stroke={YEL_D} strokeWidth={0.8}/>
      <rect x={cx - hw} y={cy - m(0.28) + hl * 0.62} width={hw * 2} height={m(0.55)}
        fill="#E0B417" stroke={YEL_D} strokeWidth={0.8}/>
      {pads.map(([dx, dy], i) => (
        <g key={i}>
          <rect x={cx + dx - padW / 2} y={cy + dy - padL / 2} width={padW} height={padL}
            fill="#B9B6AA" stroke={MUTED} strokeWidth={1} rx={1}/>
          <circle cx={cx + dx} cy={cy + dy} r={Math.max(2.5, m(0.35))} fill="#575650" stroke="#2F2E2B" strokeWidth={0.8}/>
        </g>
      ))}

      {/* Carrier deck + wheels */}
      <rect x={cx - carL / 2} y={cy - carW / 2} width={carL} height={carW}
        fill={YEL} stroke={YEL_D} strokeWidth={1.2} rx={m(0.25)}/>
      <rect x={cx - carL / 2 + m(0.4)} y={cy - carW / 2 + m(0.35)} width={carL - m(0.8)} height={carW - m(0.7)}
        fill="none" stroke={YEL_D} strokeWidth={0.5} opacity={0.5}/>
      {axles.map((f, i) => (
        <React.Fragment key={i}>
          <rect x={cx + carL * f - tyreL / 2} y={cy - carW / 2 - tyreW} width={tyreL} height={tyreW}
            fill="#3A3936" rx={1}/>
          <rect x={cx + carL * f - tyreL / 2} y={cy + carW / 2} width={tyreL} height={tyreW}
            fill="#3A3936" rx={1}/>
        </React.Fragment>
      ))}
      {/* Driver's cab, front of carrier */}
      <rect x={cx - carL / 2 + m(0.3)} y={cy - carW / 2 + m(0.3)} width={m(2.4)} height={carW - m(0.6)}
        fill="#D9DEE3" stroke="#7E8A93" strokeWidth={0.9} rx={m(0.2)}/>
      {/* Tail swing — the thing that actually catches people out on a tight compound. */}
      <circle cx={cx} cy={cy} r={3.7 * px} fill="none" stroke={MUTED} strokeWidth={1}
        strokeDasharray="4 4" opacity={0.55}/>
      {/* Upper works in METRES. These were hard-coded pixel sizes, so the house,
          counterweight and boom stayed put while the outrigger box scaled with the
          view — the crane ended up drawn roughly 13 m long against a 10 m compound
          and got worse the further you zoomed out (James, 01/08). Figures are the
          LTM 1060-3.1 class: 3.0 m house, 3.9 m counterweight slab, 3.7 m tail
          swing, 10.6 m boom retracted. */}
      {/* Slewing superstructure: counterweight aft, house, cab left of the boom,
          four-section telescopic boom tapering to the head. */}
      <g transform={`rotate(${slew} ${cx} ${cy})`}>
        <rect x={cx - m(2.6)} y={cy - m(1.95)} width={m(1.5)} height={m(3.9)}
          fill="#4A4945" stroke="#2F2E2B" strokeWidth={1} rx={m(0.15)}/>
        <rect x={cx - m(2.35)} y={cy - m(1.6)} width={m(1.0)} height={m(3.2)}
          fill="none" stroke="#6B6A66" strokeWidth={0.6}/>
        <rect x={cx - m(1.4)} y={cy - m(1.55)} width={m(3.6)} height={m(3.1)}
          fill={YEL} stroke={YEL_D} strokeWidth={1.2} rx={m(0.2)}/>
        <rect x={cx + m(0.2)} y={cy - m(1.5)} width={m(1.9)} height={m(1.15)}
          fill="#D9DEE3" stroke="#7E8A93" strokeWidth={0.9} rx={m(0.12)}/>
        {[
          { x: 0.6, len: 11.5, w: 1.30, fill: STEEL },
          { x: 2.0, len: 10.5, w: 1.06, fill: "#EFEDE6" },
          { x: 3.6, len: 9.2, w: 0.86, fill: "#F6F4EE" },
          { x: 5.4, len: 7.6, w: 0.68, fill: "#FBFAF6" },
        ].map((s, i) => (
          <rect key={i} x={cx + m(s.x)} y={cy - m(s.w / 2)} width={m(s.len)} height={m(s.w)}
            fill={s.fill} stroke={MUTED} strokeWidth={i === 0 ? 1.1 : 0.8} rx={m(0.08)}/>
        ))}
        <rect x={cx + m(12.6)} y={cy - m(0.42)} width={m(0.9)} height={m(0.84)}
          fill="#575650" stroke="#2F2E2B" strokeWidth={0.8} rx={m(0.1)}/>
        <circle cx={cx} cy={cy} r={m(0.9)} fill="none" stroke="#8E8B80" strokeWidth={0.8}/>
        <circle cx={cx} cy={cy} r={m(0.38)} fill="#575650"/>
        <circle cx={cx} cy={cy} r={m(0.14)} fill={PAPER}/>
      </g>
    </g>
  );
}

type DragTarget = { type: "compound" | "pick" | "item" | "lift" | "pan"; id?: string; ox?: number; oy?: number };

export function LiftPlanPlanView({
  st, outW = 6.30, outL = 7.35, width = 720, height = 460, id = "lp-plan",
  onChange, selectedId, onSelect, band = "unknown", pctChart = null,
}: {
  st: CanvasState; outW?: number; outL?: number; width?: number; height?: number; id?: string;
  onChange?: (next: Partial<CanvasState>) => void;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Active stage's verdict — colours the swing circle green / amber / red. */
  band?: CapacityBand;
  pctChart?: number | null;
}) {
  const bandCol = BAND_COL[band] ?? BAND_COL.unknown;
  const pctLabel = pctChart == null ? "" : `  ·  ${pctChart.toFixed(0)}% OF CHART`;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<DragTarget | null>(null);
  const editable = !!onChange;

  const px = st.scale_px_per_m;
  // Pan. Dragging empty canvas moves the whole view, so you're never stuck with
  // the site jammed against one edge and dead space on the other (James, 01/08).
  const cx = width * 0.34 + (st.pan_x_m ?? 0) * px;
  const cy = height * 0.52 + (st.pan_y_m ?? 0) * px;
  const toPx = (mx: number, my: number) => [cx + mx * px, cy + my * px] as const;

  const [compX, compY] = toPx(st.compound_x_m, st.compound_y_m);
  const [pickX, pickY] = toPx(st.pick_x_m, st.pick_y_m);
  const pickR = radiusOf(st.pick_x_m, st.pick_y_m);
  const sPos = structurePos(st);
  const [structX, structY] = toPx(sPos.x, sPos.y);
  const placeR = radiusOf(sPos.x, sPos.y);
  const setb = fenceSetbacks(st);
  const gov = Math.max(pickR, placeR) * px;
  const side = st.compound_m * px;
  const legR = (st.leg_spread_m / Math.sqrt(3)) * px;

  /** Screen → SVG user units → metres from the crane. */
  function toMetres(e: React.PointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const p = svg.createSVGPoint();
    p.x = e.clientX; p.y = e.clientY;
    const u = p.matrixTransform(ctm.inverse());
    return { x: (u.x - cx) / px, y: (u.y - cy) / px };
  }

  function onPointerDown(t: DragTarget) {
    return (e: React.PointerEvent) => {
      if (!editable) return;
      e.stopPropagation();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      setDrag(t);
      if (t.type === "item" && onSelect) onSelect(t.id ?? null);
      if (t.type !== "item" && t.type !== "compound" && onSelect) onSelect(null);
    };
  }

  /** Grab on empty canvas = pan the whole view. */
  function onBackgroundDown(e: React.PointerEvent) {
    if (!editable || !onChange) return;
    const m = toMetres(e);
    if (!m) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ type: "pan", ox: m.x, oy: m.y });
    onSelect?.(null);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!drag || !onChange) return;
    const m = toMetres(e);
    if (!m) return;
    // Free movement, to the centimetre. An earlier attempt at keeping things on
    // canvas snapped to 0.1 m and clamped, which made dragging feel notchy
    // (James, 01/08) — the off-canvas problem is better solved with a recentre
    // button than by fighting the pointer.
    const snap = (v: number) => Math.round(v * 100) / 100;
    if (drag.type === "pan") {
      // m.x/m.y are already relative to the (panned) origin, so the delta from
      // where the grab started is the pan change.
      onChange({
        pan_x_m: snap((st.pan_x_m ?? 0) + (m.x - (drag.ox ?? 0))),
        pan_y_m: snap((st.pan_y_m ?? 0) + (m.y - (drag.oy ?? 0))),
      });
      return;
    }
    if (drag.type === "compound") {
      onChange({ compound_x_m: snap(m.x), compound_y_m: snap(m.y) });
    } else if (drag.type === "pick") {
      onChange({ pick_x_m: snap(m.x), pick_y_m: snap(m.y) });
    } else if (drag.type === "item" && drag.id) {
      onChange({
        items: st.items.map(it => it.id === drag.id ? { ...it, x_m: snap(m.x), y_m: snap(m.y) } : it),
      });
    } else if (drag.type === "lift" && drag.id) {
      // Project the pointer onto the piece's own axis so the lift point slides
      // along it regardless of how the piece is rotated.
      const it = st.items.find(x => x.id === drag.id);
      if (!it) return;
      const rad = (it.rot_deg * Math.PI) / 180;
      const dx = m.x - it.x_m, dy = m.y - it.y_m;
      const along = dx * Math.cos(rad) + dy * Math.sin(rad);   // metres from centre
      const frac = Math.min(1, Math.max(0, 0.5 + along / (it.length_m || 1)));
      onChange({
        items: st.items.map(x => x.id === drag.id ? { ...x, lift_frac: Math.round(frac * 100) / 100 } : x),
      });
    }
  }

  const endDrag = () => setDrag(null);

  return (
    <svg ref={svgRef} id={id} viewBox={`0 0 ${width} ${height}`}
      style={{ width: "100%", height: "auto", background: PAPER, touchAction: "none",
               cursor: drag ? "grabbing" : "default" }}
      xmlns="http://www.w3.org/2000/svg"
      onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerLeave={endDrag}
      onPointerDown={() => editable && onSelect?.(null)}>
      {/* Near-white sheet with a 10 m grid only, drawn faintly. The old 2 m
          graph-paper grid on a warm tint was doing most of the "school project"
          look — a real drawing sits on white and gets its scale from the bar,
          not from ruled squares (James, 03/08). */}
      <defs>
        <pattern id={`${id}-grid`} width={px * 10} height={px * 10} patternUnits="userSpaceOnUse">
          <path d={`M${px * 10} 0H0V${px * 10}`} fill="none" stroke="#ECEBE6" strokeWidth={0.8}/>
        </pattern>
      </defs>
      <rect width={width} height={height} fill="#FCFCFB"
        onPointerDown={onBackgroundDown}
        style={{ cursor: editable ? "move" : "default" }}/>
      <rect width={width} height={height} fill={`url(#${id}-grid)`}
        style={{ pointerEvents: "none" }}/>
      {/* Site backdrop — an FC extract or aerial, scaled to metres and pinned to
          the crane, so the drawing sits on the real site rather than a blank
          grid. Drawn first so everything else reads over it. */}
      {st.background_url && (() => {
        const bgPx = st.bg_scale_px_per_m ?? 1;      // image px per metre
        const w = 2000 / bgPx * px;                   // assume ~2000px wide import
        const [bx, by] = toPx(st.bg_x_m ?? 0, st.bg_y_m ?? 0);
        return (
          <image href={st.background_url} x={bx - w / 2} y={by - (w * 0.62) / 2}
            width={w} height={w * 0.62} preserveAspectRatio="xMidYMid meet"
            opacity={st.bg_opacity ?? 0.45} style={{ pointerEvents: "none" }}/>
        );
      })()}

      {/* Exclusion zone reads as a BOUNDARY, in slate — it's a keep-out line,
          not a capacity verdict, and having it warm made it compete with the
          swing circle for the same meaning (James, 02/08). */}
      {st.exclusion_r_m > 0 && (
        <g style={{ pointerEvents: "none" }}>
          <circle cx={cx} cy={cy} r={st.exclusion_r_m * px} fill="none"
            stroke={SLATE} strokeWidth={1.1} strokeDasharray="9 6"/>
          <text x={cx} y={cy - st.exclusion_r_m * px + 13} textAnchor="middle" fontSize={9} fill={SLATE}
            letterSpacing="0.4"
            stroke={PAPER} strokeWidth={3} strokeLinejoin="round" paintOrder="stroke">
            EXCLUSION ZONE {st.exclusion_r_m} m
          </text>
        </g>
      )}
      {/* Swing circle carries the traffic light: green while there's real
          margin, amber approaching the chart, red over. Kept subtle — the fill
          is barely there, the colour lives in the stroke and the label. */}
      <g style={{ pointerEvents: "none" }}>
        {/* Stroke only — the tinted fill washed the whole sheet in colour.
            The ring earns its green/amber/red; nothing else should compete. */}
        <circle cx={cx} cy={cy} r={gov} fill="none" stroke={bandCol} strokeWidth={1.8}/>
        <text x={cx} y={cy - gov + 13} textAnchor="middle" fontSize={9} fill={bandCol} fontWeight="bold"
          letterSpacing="0.4"
          stroke={PAPER} strokeWidth={3} strokeLinejoin="round" paintOrder="stroke">
          GOVERNING RADIUS {(gov / px).toFixed(1)} m{pctLabel}
        </text>
      </g>

      {/* Compound — drag to move, rotate from the inspector */}
      {/* The compound is selectable like any other object, so it can be rotated
          and adjusted from the same control strip. It was the one thing you
          couldn't select, and it's the thing most often needing a rotate
          (James, 01/08). COMPOUND_ID is the sentinel the editor looks for. */}
      <g transform={`rotate(${st.compound_rotation_deg} ${compX} ${compY})`}
        onPointerDown={e => { onSelect?.(COMPOUND_ID); onPointerDown({ type: "compound" })(e); }}
        style={{ cursor: editable ? "grab" : "default" }}>
        <rect x={compX - side / 2} y={compY - side / 2} width={side} height={side}
          fill="#F1F0EC" fillOpacity={0.85}
          stroke={selectedId === COMPOUND_ID ? ACCENT : "#A8A599"}
          strokeWidth={selectedId === COMPOUND_ID ? 2.5 : 1.5}/>
      </g>

      {/* Structure / footings, at their real offset within the pad */}
      {st.structure === "lattice" ? (
        <g transform={`rotate(${st.compound_rotation_deg} ${structX} ${structY})`} style={{ pointerEvents: "none" }}>
          <path d={`M${structX} ${structY - legR} L${structX + legR * 0.866} ${structY + legR * 0.5} L${structX - legR * 0.866} ${structY + legR * 0.5} Z`}
            fill="none" stroke={MUTED} strokeWidth={1.2} strokeDasharray="4 3"/>
          {[[0, -legR], [legR * 0.866, legR * 0.5], [-legR * 0.866, legR * 0.5]].map(([dx, dy], i) => (
            <circle key={i} cx={structX + dx} cy={structY + dy} r={7} fill="#B9B6AA" stroke="#6E6C64" strokeWidth={1.2}/>
          ))}
        </g>
      ) : (
        <g style={{ pointerEvents: "none" }}>
          <circle cx={structX} cy={structY} r={1.5 * px} fill="#D8D5C9" stroke={MUTED} strokeWidth={1.2}/>
          <circle cx={structX} cy={structY} r={0.6 * px} fill="#B9B6AA" stroke="#6E6C64" strokeWidth={1.2}/>
        </g>
      )}

      {/* Centre-of-structure mark, drawing-office style: a survey cross that
          reads as the setout point. James asked for the centre of the pole to be
          unmistakable in the compound (01/08). */}
      <g style={{ pointerEvents: "none" }}>
        <line x1={structX - 1.05 * px} y1={structY} x2={structX + 1.05 * px} y2={structY} stroke={INK} strokeWidth={0.9}/>
        <line x1={structX} y1={structY - 1.05 * px} x2={structX} y2={structY + 1.05 * px} stroke={INK} strokeWidth={0.9}/>
        <circle cx={structX} cy={structY} r={0.28 * px} fill={INK}/>
        {(() => {
          const lx = structX + 1.35 * px + (st.structure_label_dx_m ?? 0) * px;
          const ly = structY - 0.55 * px + (st.structure_label_dy_m ?? 0) * px;
          const moved = !!(st.structure_label_dx_m || st.structure_label_dy_m);
          return (
            <>
              {moved && <line x1={structX} y1={structY} x2={lx - 3} y2={ly + 2}
                stroke={MUTED} strokeWidth={0.7} strokeDasharray="3 2"/>}
              <text x={lx} y={ly} fontSize={8} fill={INK}
                stroke={PAPER} strokeWidth={2.6} strokeLinejoin="round" paintOrder="stroke">℄ STRUCTURE</text>
              <text x={lx} y={ly} fontSize={8} fill={INK}>℄ STRUCTURE</text>
              {(st.structure_dx_m || st.structure_dy_m) ? (
                <text x={lx} y={ly + 1.45 * px} fontSize={7.5} fill={MUTED}
                  stroke={PAPER} strokeWidth={2.4} strokeLinejoin="round" paintOrder="stroke">
                  setback {setb.west.toFixed(1)} / {setb.east.toFixed(1)} / {setb.north.toFixed(1)} / {setb.south.toFixed(1)} m (W/E/N/S)
                </text>
              ) : null}
            </>
          );
        })()}
      </g>

      {/* Setout dimension: slew centre to structure centre, drawn as a proper
          dimension line with ticks rather than another floating label. */}
      <g style={{ pointerEvents: "none" }}>
        <line x1={cx} y1={cy} x2={structX} y2={structY} stroke={ACCENT} strokeWidth={0.9} strokeDasharray="6 3"/>
        <circle cx={cx} cy={cy} r={2.5} fill={ACCENT}/>
        {/* No text here — the "place N m" label below is the same dimension and
            the two were printing on top of each other (James, 01/08). */}
      </g>

      <text x={compX} y={compY + side / 2 + 13} textAnchor="middle" fontSize={9.5} fill={MUTED}
        stroke={PAPER} strokeWidth={3} strokeLinejoin="round" paintOrder="stroke"
        style={{ pointerEvents: "none" }}>
        SITE COMPOUND {st.compound_m.toFixed(1)} × {st.compound_m.toFixed(1)} m
      </text>
      <text x={compX} y={compY + side / 2 + 13} textAnchor="middle" fontSize={9.5} fill={MUTED}
        style={{ pointerEvents: "none" }}>
        SITE COMPOUND {st.compound_m.toFixed(1)} × {st.compound_m.toFixed(1)} m
      </text>

      {/* Placed items. The label is counter-rotated so it stays readable at any
          orientation, and sits INSIDE the piece when it fits. The orange dot is
          the lift point — drag it along the piece to move the hook (tail, centre
          for a dual-chain lift, or head end for a tapered pole). */}
      {st.items.map(it => {
        const [ix, iy] = toPx(it.x_m, it.y_m);
        const w = it.length_m * px, h = it.width_m * px;
        const sel = selectedId === it.id;
        const frac = it.lift_frac ?? 0.5;
        const lx = ix - w / 2 + w * frac;          // lift point in local (unrotated) space
        const fits = w > 54 && h >= 13;            // room for the label inside?
        return (
          <g key={it.id} transform={`rotate(${it.rot_deg} ${ix} ${iy})`}>
            <g onPointerDown={onPointerDown({ type: "item", id: it.id })}
              style={{ cursor: editable ? "grab" : "default" }}>
              {(() => {
                const s = ITEM_STYLE[it.kind] ?? ITEM_STYLE.note;
                return (
                  <>
                    <rect x={ix - w / 2} y={iy - h / 2} width={w} height={h} rx={2}
                      fill={s.fill} fillOpacity={s.op ?? 1}
                      stroke={sel ? ACCENT : s.stroke} strokeWidth={sel ? 2 : 1.2}
                      strokeDasharray={sel ? undefined : s.dash}/>
                    {it.kind === "lattice_module" && (
                      <>
                        <line x1={ix - w / 2} y1={iy - h / 2} x2={ix + w / 2} y2={iy + h / 2} stroke="#B4B2A9" strokeWidth={1}/>
                        <line x1={ix + w / 2} y1={iy - h / 2} x2={ix - w / 2} y2={iy + h / 2} stroke="#B4B2A9" strokeWidth={1}/>
                      </>
                    )}
                    {/* Hatch the areas so they read as ground, not as objects */}
                    {(it.kind === "laydown" || it.kind === "hardstand" || it.kind === "access") &&
                      Array.from({ length: Math.max(0, Math.floor(w / 14)) }).map((_, k) => (
                        <line key={k} x1={ix - w / 2 + k * 14 + 7} y1={iy - h / 2}
                          x2={ix - w / 2 + k * 14 + 7 - h} y2={iy + h / 2}
                          stroke={s.stroke} strokeWidth={0.5} opacity={0.35}/>
                      ))}
                  </>
                );
              })()}
            </g>

            {/* Lift point — only meaningful on something you actually lift */}
            {(it.kind === "pole_section" || it.kind === "lattice_module" || it.kind === "plant") && (
              <g onPointerDown={onPointerDown({ type: "lift", id: it.id })}
                style={{ cursor: editable ? "ew-resize" : "default" }}>
                <circle cx={lx} cy={iy} r={editable ? 6.5 : 3.5} fill={ACCENT} fillOpacity={editable ? 0.18 : 1}
                  stroke={ACCENT} strokeWidth={editable ? 1.2 : 0}/>
                <circle cx={lx} cy={iy} r={3} fill={ACCENT}/>
              </g>
            )}

            {/* Label. Inside when it genuinely fits, otherwise on a short leader
                clear of the shape — labels used to stack on top of each other
                whenever two items were close (James, 01/08). A halo behind the
                text keeps it readable over hatching. */}
            {it.label && (() => {
              // Nudging the label pulls it clear of whatever is covering it and
              // draws a leader back to the shape, so nothing ends up buried
              // under another object (James, 01/08).
              const ldx = (it.label_dx_m ?? 0) * px, ldy = (it.label_dy_m ?? 0) * px;
              const moved = ldx !== 0 || ldy !== 0;
              const baseY = fits && !moved ? iy + 3 : iy - h / 2 - 7;
              const lx2 = ix + ldx, ly = baseY + ldy;
              const leader = moved || !fits;
              return (
                <g style={{ pointerEvents: "none" }}>
                  {leader && (
                    <line x1={ix} y1={iy} x2={lx2} y2={ly + 2}
                      stroke={sel ? ACCENT : MUTED} strokeWidth={0.7} strokeDasharray={moved ? "3 2" : undefined}/>
                  )}
                  {moved && <circle cx={lx2} cy={ly + 2} r={1.6} fill={sel ? ACCENT : MUTED}/>}
                  <g transform={`rotate(${-it.rot_deg} ${lx2} ${ly})`}>
                    <text x={lx2} y={ly} textAnchor="middle" fontSize={8.5}
                      stroke={PAPER} strokeWidth={3.2} strokeLinejoin="round" opacity={0.95}>{it.label}</text>
                    <text x={lx2} y={ly} textAnchor="middle" fontSize={8.5} fontWeight={fits && !moved ? 500 : 600}
                      fill={sel ? ACCENT : "#5F5E5A"}>{it.label}</text>
                  </g>
                </g>
              );
            })()}
          </g>
        );
      })}

      {/* Radius lines */}
      <line x1={cx} y1={cy} x2={pickX} y2={pickY} stroke="#185FA5" strokeWidth={1.4} strokeDasharray="4 3"
        style={{ pointerEvents: "none" }}/>
      <line x1={cx} y1={cy} x2={structX} y2={structY} stroke="#0F6E56" strokeWidth={1.4} strokeDasharray="4 3"
        style={{ pointerEvents: "none" }}/>
      {/* Both dimension labels get a paper halo and sit a third of the way along
          their own line, so they can't stack on each other near the crane. */}
      {([
        // Pulled right out to the markers. At the midpoint they sat under the
        // crane body and were unreadable (James, 01/08).
        { x: cx + (pickX - cx) * 0.88, y: cy + (pickY - cy) * 0.88 - 12, t: `pick ${pickR.toFixed(1)} m`, c: "#0C447C" },
        { x: cx + (structX - cx) * 0.86, y: cy + (structY - cy) * 0.86 - 12, t: `place ${placeR.toFixed(1)} m`, c: "#04342C" },
      ]).map((l, i) => (
        <g key={i} style={{ pointerEvents: "none" }}>
          <text x={l.x} y={l.y - 5} textAnchor="middle" fontSize={10} fill={l.c}
            stroke={PAPER} strokeWidth={3.2} strokeLinejoin="round" paintOrder="stroke">{l.t}</text>
          <text x={l.x} y={l.y - 5} textAnchor="middle" fontSize={10} fontWeight="bold" fill={l.c}>{l.t}</text>
        </g>
      ))}

      {/* Pick handle — drag to set the pick radius */}
      <g onPointerDown={onPointerDown({ type: "pick" })} style={{ cursor: editable ? "grab" : "default" }}>
        <circle cx={pickX} cy={pickY} r={11} fill="#E6F1FB" stroke="#185FA5" strokeWidth={2}/>
        <text x={pickX} y={pickY + 3.5} textAnchor="middle" fontSize={9} fill="#0C447C"
          style={{ pointerEvents: "none" }}>P</text>
      </g>

      <CranePlan cx={cx} cy={cy} px={px} slew={st.slew_deg} outW={outW} outL={outL}
        heading={st.crane_heading_deg ?? 0}/>

      <g style={{ pointerEvents: "none" }}>
        <line x1={28} y1={height - 24} x2={28 + 10 * px} y2={height - 24} stroke="#5F5E5A" strokeWidth={1.5}/>
        <line x1={28} y1={height - 29} x2={28} y2={height - 19} stroke="#5F5E5A" strokeWidth={1.5}/>
        <line x1={28 + 10 * px} y1={height - 29} x2={28 + 10 * px} y2={height - 19} stroke="#5F5E5A" strokeWidth={1.5}/>
        <text x={28 + 5 * px} y={height - 31} textAnchor="middle" fontSize={10} fill="#5F5E5A">10 m</text>
      </g>
      {/* Legend. Without it the rings and dashed lines are just decoration —
          a reviewer has no way to read the drawing (James, 01/08). */}
      {(() => {
        const rows: [string, string, string][] = [
          ["ring", bandCol, `Governing radius — ${(gov / px).toFixed(1)} m${
            band === "ok" ? " (within chart)" : band === "caution" ? " (approaching chart)"
            : band === "over" ? " (OVER CHART)" : ""}`],
          ...(st.exclusion_r_m > 0 ? [["dash", SLATE, `Exclusion zone — ${st.exclusion_r_m} m`] as [string, string, string]] : []),
          ["dash", "#0C447C", "Pick — where the load is lifted from"],
          ["dash", "#04342C", "Place — structure centreline"],
          ["cross", INK, "℄ setout point"],
        ];
        const bw = 218, bh = 16 + rows.length * 14;
        const bx = width - bw - 10, by = height - bh - 10;
        return (
          <g style={{ pointerEvents: "none" }}>
            <rect x={bx} y={by} width={bw} height={bh} rx={3} fill={PAPER} fillOpacity={0.94}
              stroke={LINE} strokeWidth={1}/>
            <text x={bx + 8} y={by + 13} fontSize={8} fill={MUTED} fontWeight="bold" letterSpacing="0.6">LEGEND</text>
            {rows.map(([kind, colour, label], i) => {
              const y = by + 26 + i * 14;
              return (
                <g key={i}>
                  {kind === "ring" && <circle cx={bx + 15} cy={y - 3} r={5} fill="none" stroke={colour} strokeWidth={1.5}/>}
                  {kind === "dash" && <line x1={bx + 8} y1={y - 3} x2={bx + 22} y2={y - 3} stroke={colour} strokeWidth={1.4} strokeDasharray="4 3"/>}
                  {kind === "cross" && (
                    <>
                      <line x1={bx + 9} y1={y - 3} x2={bx + 21} y2={y - 3} stroke={colour} strokeWidth={0.9}/>
                      <line x1={bx + 15} y1={y - 9} x2={bx + 15} y2={y + 3} stroke={colour} strokeWidth={0.9}/>
                    </>
                  )}
                  <text x={bx + 28} y={y} fontSize={8} fill="#5F5E5A">{label}</text>
                </g>
              );
            })}
          </g>
        );
      })()}
      {editable && (
        <text className="lp-noexport" x={width - 10} y={16} textAnchor="end" fontSize={9} fill="#B4B2A9">
          drag the compound, the pick point and any section
        </text>
      )}
    </svg>
  );
}

/**
 * Elevation for ONE stage. Draws the piece THIS lift is placing, standing on
 * whatever has already been erected — not the finished structure. Stage 1
 * standing a 9 m base section is a 9 m problem; the 30 m pole only exists by
 * the last stage, and pretending otherwise makes the hook height look wrong.
 */
export function LiftPlanElevation({
  st, boomM, radiusM, pieceLengthM = 0, builtHeightM = 0, stageLabel,
  width = 720, height = 300, id = "lp-elev",
}: {
  st: CanvasState; boomM: number; radiusM: number;
  pieceLengthM?: number; builtHeightM?: number; stageLabel?: string;
  width?: number; height?: number; id?: string;
}) {
  // Only scale to the finished structure if this stage actually reaches it.
  const topOfLift = builtHeightM + pieceLengthM;
  const showM = topOfLift > 0 ? topOfLift : st.structure_height_m;
  const maxM = Math.max(boomM * 1.05, showM * 1.25, 20);
  const px = (height - 60) / maxM;
  const groundY = height - 40;
  const cx = width * 0.28;
  const angle = boomAngleDeg(boomM, radiusM);
  const hookH = hookHeightM(boomM, radiusM);
  const pivotY = groundY - 2.1 * px;
  const tipX = cx + radiusM * px;
  const tipY = hookH != null ? groundY - hookH * px : pivotY - boomM * px;
  const grad = st.ground_gradient_pct / 100;
  const rightGroundY = groundY - (width - cx) * grad * 0.35;
  // Pole drawn to a nominal 1.2 m face, on the centreline at the place radius.
  const poleHalf = Math.max(4, 0.6 * px);
  const topOfLiftY = groundY - topOfLift * px;
  // Fall = hook height less the top of what we're landing. Hook block parks just
  // above the piece so the rope visibly connects to the load being placed.
  const ropeM = hookH != null && topOfLift > 0 ? hookH - topOfLift : null;
  const hookY = pieceLengthM > 0
    ? Math.min(Math.max(tipY + 12, topOfLiftY - 12), groundY - 8)
    : tipY + 26;

  return (
    <svg id={id} viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", background: PAPER }}
      xmlns="http://www.w3.org/2000/svg">
      <rect width={width} height={height} fill={PAPER}/>
      {[10, 20, 30, 40, 50, 60].filter(m => m <= maxM).map(m => (
        <g key={m}>
          <line x1={0} y1={groundY - m * px} x2={width} y2={groundY - m * px} stroke="#E6E4DC" strokeWidth={1}/>
          <text x={4} y={groundY - m * px - 3} fontSize={9} fill="#B4B2A9">{m} m</text>
        </g>
      ))}
      <line x1={0} y1={groundY} x2={width} y2={rightGroundY} stroke={INK} strokeWidth={1.6}/>
      {st.ground_gradient_pct !== 0 && (
        <text x={width - 8} y={rightGroundY - 6} textAnchor="end" fontSize={9} fill={MUTED}>
          {st.ground_gradient_pct > 0 ? "+" : ""}{st.ground_gradient_pct}% grade
        </text>
      )}
      {/* ---- Structure, drawn ON the centreline ----------------------------
          The load lands on the structure centreline, and that centreline IS the
          place radius — so the hook hangs directly over it. This used to be
          offset 26 px right of the boom tip, which made the hook look like it
          was dangling behind the pole and never touching the load (James,
          01/08). Structure is drawn before the crane so the boom passes over
          it, which is what you'd see from the side. */}
      {st.structure_height_m > topOfLift && (
        <>
          <rect x={tipX - poleHalf} y={groundY - st.structure_height_m * px} width={poleHalf * 2}
            height={st.structure_height_m * px} fill="none" stroke="#D8D5C9" strokeWidth={1}
            strokeDasharray="3 4"/>
          <text x={tipX + poleHalf + 8} y={groundY - st.structure_height_m * px + 10} fontSize={9} fill="#B4B2A9">
            {st.structure_height_m} m finished
          </text>
        </>
      )}
      {builtHeightM > 0 && (
        <>
          <rect x={tipX - poleHalf} y={groundY - builtHeightM * px} width={poleHalf * 2}
            height={builtHeightM * px} fill="#C9C6BB" stroke={MUTED} strokeWidth={1.2}/>
          <text x={tipX + poleHalf + 8} y={groundY - builtHeightM * px + 12} fontSize={9} fill={MUTED}>
            {builtHeightM} m already erected
          </text>
        </>
      )}
      {pieceLengthM > 0 && (
        <>
          <rect x={tipX - poleHalf} y={topOfLiftY} width={poleHalf * 2}
            height={pieceLengthM * px} fill={YEL} fillOpacity={0.65} stroke={YEL_D} strokeWidth={1.4}/>
          <line x1={tipX - poleHalf - 10} y1={topOfLiftY} x2={tipX + poleHalf + 10} y2={topOfLiftY}
            stroke={YEL_D} strokeWidth={1} strokeDasharray="3 3"/>
          <text x={tipX + poleHalf + 8} y={topOfLiftY - 6} fontSize={10} fill={YEL_D}>
            {stageLabel ? `${stageLabel} — ` : ""}{pieceLengthM} m this lift,
            top of stack {(builtHeightM + pieceLengthM).toFixed(2)} m
          </text>
        </>
      )}

      {/* ---- The machine, side on ------------------------------------------
          Carrier on its tyres with the outriggers jacked down, counterweight
          slab aft, cab, and a four-section telescopic boom that tapers to the
          head sheave. Drawn in metres so it holds scale against the structure. */}
      <g>
        {[-4.6, -3.5, 3.3, 4.4].map((dx, i) => (
          <circle key={i} cx={cx + dx * px} cy={groundY - 0.62 * px} r={0.62 * px}
            fill="#3A3936" stroke="#22211F" strokeWidth={0.8}/>
        ))}
        {[-5.6, 5.2].map((dx, i) => (
          <g key={i}>
            <rect x={cx + dx * px - 0.16 * px} y={groundY - 1.9 * px} width={0.32 * px} height={1.9 * px}
              fill="#8E8B80" stroke={MUTED} strokeWidth={0.7}/>
            <rect x={cx + dx * px - 0.8 * px} y={groundY - 0.3 * px} width={1.6 * px} height={0.3 * px}
              fill="#B9B6AA" stroke={MUTED} strokeWidth={0.9}/>
          </g>
        ))}
        <rect x={cx - 6.2 * px} y={groundY - 1.9 * px} width={12.4 * px} height={1.0 * px}
          fill={YEL} stroke={YEL_D} strokeWidth={1.1} rx={0.12 * px}/>
        <rect x={cx - 6.1 * px} y={groundY - 2.9 * px} width={2.2 * px} height={1.05 * px}
          fill="#D9DEE3" stroke="#7E8A93" strokeWidth={0.9} rx={0.12 * px}/>
        <rect x={cx - 3.9 * px} y={pivotY - 0.15 * px} width={7.4 * px} height={0.9 * px}
          fill={YEL} stroke={YEL_D} strokeWidth={1} rx={0.1 * px}/>
        <rect x={cx - 3.7 * px} y={pivotY - 2.5 * px} width={1.6 * px} height={2.4 * px}
          fill="#4A4945" stroke="#2F2E2B" strokeWidth={1} rx={0.1 * px}/>
        <rect x={cx - 1.9 * px} y={pivotY - 2.3 * px} width={2.0 * px} height={2.2 * px}
          fill={YEL} stroke={YEL_D} strokeWidth={1} rx={0.12 * px}/>
        <rect x={cx + 0.2 * px} y={pivotY - 2.2 * px} width={1.1 * px} height={1.6 * px}
          fill="#D9DEE3" stroke="#7E8A93" strokeWidth={0.9} rx={0.1 * px}/>

        {/* Telescopic boom — nested sections along the boom axis */}
        {[
          { f0: 0.00, f1: 1.00, w: 1.30, fill: STEEL, sw: 1.1 },
          { f0: 0.14, f1: 0.96, w: 1.02, fill: "#EFEDE6", sw: 0.7 },
          { f0: 0.32, f1: 0.93, w: 0.78, fill: "#F6F4EE", sw: 0.7 },
          { f0: 0.54, f1: 0.90, w: 0.58, fill: "#FBFAF6", sw: 0.7 },
        ].map((s, i) => {
          const x1 = cx + (tipX - cx) * s.f0, y1 = pivotY + (tipY - pivotY) * s.f0;
          const x2 = cx + (tipX - cx) * s.f1, y2 = pivotY + (tipY - pivotY) * s.f1;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={s.fill} strokeWidth={Math.max(2, s.w * px)} strokeLinecap="butt"/>;
        })}
        <line x1={cx} y1={pivotY} x2={tipX} y2={tipY} stroke={MUTED} strokeWidth={0.7} opacity={0.65}/>
        <circle cx={tipX} cy={tipY} r={Math.max(3, 0.45 * px)} fill="#575650" stroke="#2F2E2B" strokeWidth={0.8}/>
        <circle cx={cx} cy={pivotY} r={Math.max(3.5, 0.5 * px)} fill="#575650"/>
        {angle != null && (
          <text x={cx + 1.6 * px} y={pivotY - 2.9 * px} fontSize={10} fill={ACCENT} fontWeight="bold">
            {angle.toFixed(1)}°
          </text>
        )}
      </g>

      {/* ---- The fall: boom head down to the load it's actually holding ---- */}
      <line x1={tipX} y1={tipY} x2={tipX} y2={hookY} stroke={INK} strokeWidth={1.4}/>
      <circle cx={tipX} cy={hookY + 4} r={4.5} fill="#111"/>
      {ropeM != null && ropeM > 0.5 && (
        <text x={tipX - 6} y={(tipY + hookY) / 2} textAnchor="end" fontSize={9} fill={MUTED}>
          fall {ropeM.toFixed(1)} m
        </text>
      )}

      {hookH != null && (
        <>
          <line x1={tipX} y1={tipY} x2={width - 14} y2={tipY} stroke={LINE} strokeWidth={1} strokeDasharray="3 3"/>
          <text x={width - 14} y={tipY - 5} textAnchor="end" fontSize={10} fill={ACCENT}>hook {hookH.toFixed(1)} m</text>
        </>
      )}
      <line x1={cx} y1={groundY + 12} x2={tipX} y2={groundY + 12} stroke={MUTED} strokeWidth={1}/>
      <text x={(cx + tipX) / 2} y={groundY + 24} textAnchor="middle" fontSize={10} fill={MUTED}>
        radius {radiusM.toFixed(1)} m
      </text>
    </svg>
  );
}

/**
 * Capacity curve — utilisation across the whole charted radius range for the
 * chosen boom length, with the current lift marked. Shows at a glance how much
 * headroom is left and exactly where the lift tips from green into amber and
 * over the line, which a single percentage never tells you.
 */
export function CapacityCurve({
  points, boomM, variant = "", grossKg, currentRadiusM, width = 640, height = 150, id = "lp-curve",
}: {
  points: { boom_length_m: number; radius_m: number; variant: string; capacity_kg: number }[];
  boomM: number; variant?: string; grossKg: number; currentRadiusM: number;
  width?: number; height?: number; id?: string;
}) {
  const col = points
    .filter(p => Number(p.boom_length_m) === Number(boomM) && (p.variant ?? "") === variant)
    .sort((a, b) => a.radius_m - b.radius_m);
  if (col.length < 2 || grossKg <= 0) {
    return (
      <div style={{ fontSize: 12, color: "#8E8B80", padding: "10px 0" }}>
        Pick a boom length and enter a weight to see the capacity curve.
      </div>
    );
  }

  const padL = 34, padR = 10, padT = 10, padB = 22;
  const rMin = col[0].radius_m, rMax = col[col.length - 1].radius_m;
  const pts = col.map(p => ({ r: p.radius_m, pct: (grossKg / p.capacity_kg) * 100 }));
  const capPct = Math.min(160, Math.max(110, ...pts.map(p => p.pct)));
  const x = (r: number) => padL + ((r - rMin) / (rMax - rMin)) * (width - padL - padR);
  const y = (pct: number) => padT + (1 - Math.min(pct, capPct) / capPct) * (height - padT - padB);

  const band = (from: number, to: number, fill: string) => {
    const yTo = y(Math.min(to, capPct)), yFrom = y(Math.min(from, capPct));
    return <rect x={padL} y={yTo} width={width - padL - padR} height={Math.max(yFrom - yTo, 0)} fill={fill}/>;
  };

  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.r).toFixed(1)} ${y(p.pct).toFixed(1)}`).join(" ");
  const here = pts.reduce((best, p) =>
    Math.abs(p.r - currentRadiusM) < Math.abs(best.r - currentRadiusM) ? p : best, pts[0]);
  const inRange = currentRadiusM >= rMin && currentRadiusM <= rMax;
  const hereX = inRange ? x(currentRadiusM) : null;
  const herePct = here.pct;

  return (
    <svg id={id} viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}
      xmlns="http://www.w3.org/2000/svg">
      {band(0, 75, "#EAF3DE")}
      {band(75, 90, "#FAEEDA")}
      {band(90, capPct, "#FCEBEB")}
      <line x1={padL} y1={y(100)} x2={width - padR} y2={y(100)} stroke="#E24B4A" strokeWidth={1} strokeDasharray="4 3"/>
      <text x={padL - 4} y={y(100) + 3} textAnchor="end" fontSize={8.5} fill="#A32D2D">100%</text>
      <text x={padL - 4} y={y(75) + 3} textAnchor="end" fontSize={8.5} fill="#639922">75%</text>
      <path d={path} fill="none" stroke="#3F3E3A" strokeWidth={1.8}/>
      {hereX != null && (
        <>
          <line x1={hereX} y1={padT} x2={hereX} y2={height - padB} stroke="#FF6A1A" strokeWidth={1.2}/>
          <circle cx={hereX} cy={y(herePct)} r={4.5} fill="#FF6A1A"/>
          <text x={hereX} y={padT + 10} textAnchor={hereX > width * 0.7 ? "end" : "start"} fontSize={10} fill="#FF6A1A">
            {" "}{herePct.toFixed(0)}% @ {currentRadiusM.toFixed(1)} m
          </text>
        </>
      )}
      <line x1={padL} y1={height - padB} x2={width - padR} y2={height - padB} stroke="#C9C6BB" strokeWidth={1}/>
      {[rMin, (rMin + rMax) / 2, rMax].map((r, i) => (
        <text key={i} x={x(r)} y={height - padB + 13} textAnchor="middle" fontSize={9} fill="#8E8B80">
          {r.toFixed(0)} m
        </text>
      ))}
    </svg>
  );
}

export async function svgToPng(svgId: string, scale = 2): Promise<string | null> {
  const el = document.getElementById(svgId) as SVGSVGElement | null;
  if (!el) return null;
  const vb = el.getAttribute("viewBox")?.split(/\s+/).map(Number) ?? [0, 0, 720, 460];
  const w = vb[2], h = vb[3];
  const clone = el.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("width", String(w));
  clone.setAttribute("height", String(h));
  // Editor affordances are not part of the deliverable. "drag the compound, the
  // pick point and any section" was printing into the client's lift study
  // (James, 01/08) — anything marked lp-noexport is stripped from the export.
  clone.querySelectorAll(".lp-noexport").forEach(n => n.remove());
  const xml = new XMLSerializer().serializeToString(clone);
  const src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = w * scale; c.height = h * scale;
      const ctx = c.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
